import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type ChatInitData, type UserPersonaTaskSummary } from '../shared/api-client';
import './DashboardBoardDestinations.css';

/** Frame 5b — Task Templates gallery, grouped by persona + a "Mine" section. */
export function TaskTemplatesBoard({ onClose }: { onClose: () => void }): JSX.Element {
  const [init, setInit] = useState<ChatInitData | null>(null);
  const [myTasks, setMyTasks] = useState<UserPersonaTaskSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([api.getChatInit(), api.listMyPersonaTasks()]).then(([initRes, myRes]) => {
      if (initRes.success) setInit(initRes.data);
      else setError(initRes.error.message);
      if (myRes.success) setMyTasks(myRes.data);
      setLoading(false);
    });
  }, []);

  const personaSections = init?.personas.filter((p) => p.tasks.length > 0) ?? [];
  const myTaskCount = myTasks.length;

  return (
    <div className="ttb">
      <div className="ttb-head">
        <button type="button" className="ttb-back" onClick={onClose} aria-label="Back to dashboard">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>
        <div className="ttb-head-title">
          <h1 className="ttb-h1">Task Templates</h1>
          <p className="ttb-sub">Structured prompts with their own fields and tool sets</p>
        </div>
        <Link to="/apps/chat/settings/tasks/new" className="ttb-new">New template</Link>
      </div>
      {loading ? (
        <div className="ttb-loading">Loading templates…</div>
      ) : error ? (
        <div className="ttb-error">{error}</div>
      ) : (
        <div className="ttb-sections">
          {myTaskCount > 0 && (
            <section className="ttb-section">
              <header className="ttb-section-head"><h2 className="ttb-section-title">Mine · {myTaskCount}</h2></header>
              <div className="ttb-grid">
                {myTasks.map((t) => (
                  <Link key={t.id} to={`/apps/chat?myTask=${t.id}`} className="ttb-card">
                    <div className="ttb-card-head">
                      <span className="ttb-card-icon" aria-hidden="true">
                        <span className="material-icons">{t.icon || 'edit_note'}</span>
                      </span>
                      <span className="ttb-card-title">{t.name}</span>
                    </div>
                    {t.description && <p className="ttb-card-desc">{t.description}</p>}
                  </Link>
                ))}
              </div>
            </section>
          )}
          {personaSections.map((p) => (
            <section key={p.id} className="ttb-section">
              <header className="ttb-section-head"><h2 className="ttb-section-title">{p.name} · {p.tasks.length}</h2></header>
              <div className="ttb-grid">
                {p.tasks.map((t) => (
                  <Link key={t.id} to={`/apps/chat?task=${t.id}`} className="ttb-card">
                    <div className="ttb-card-head">
                      <span className="ttb-card-icon" aria-hidden="true">
                        <span className="material-icons">{t.icon || 'assignment'}</span>
                      </span>
                      <span className="ttb-card-title">{t.name}</span>
                    </div>
                    {t.description && <p className="ttb-card-desc">{t.description}</p>}
                  </Link>
                ))}
              </div>
            </section>
          ))}
          {personaSections.length === 0 && myTaskCount === 0 && (
            <div className="ttb-empty">No task templates yet.</div>
          )}
        </div>
      )}
    </div>
  );
}
